import { useState, type ReactNode } from "react";

import type { ApiClient } from "../../shared/api/client";
import { daysAgo, pluralCategory, useLocale, useT } from "../../shared/i18n";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  GroupLabel,
  Icons,
  IconButton,
  Input,
  LargeTitle,
  ListRow,
  Sheet,
} from "../../shared/ui";
import { controlClassName } from "../../shared/ui/controlClassName";
import { EditSectionSheet } from "./EditSectionSheet";
import { isSystemSection, type Section } from "./sections";

const RECENT = 3;

/** Every section in the Workspace: recent first, then system, then the owner's own. */
/** One to three words: the same rule as the server's name validator. */
const MAX_NAME_WORDS = 3;

const RENAME_ERRORS = {
  ability_name_too_long: "sections.renameTooLong",
  ability_name_taken: "sections.renameTaken",
} as const;

function renameErrorKey(
  error: unknown,
): "sections.renameError" | (typeof RENAME_ERRORS)[keyof typeof RENAME_ERRORS] {
  const cause = error instanceof Error ? error.cause : undefined;
  const code =
    typeof cause === "object" && cause !== null && "code" in cause
      ? (cause as { code?: unknown }).code
      : undefined;
  return typeof code === "string" && code in RENAME_ERRORS
    ? RENAME_ERRORS[code as keyof typeof RENAME_ERRORS]
    : "sections.renameError";
}

export function SectionList({
  sections,
  visits,
  onOpen,
  onCreate,
  onRename,
  onOpenDraft,
  onBackToChat,
  history,
  keywords,
  now = new Date(),
}: {
  sections: Section[];
  /** Section id → when this device last opened it. */
  visits: Record<string, string>;
  onOpen: (section: Section) => void;
  /** New sections are described to the assistant in the chat. */
  onCreate: () => void;
  /** Absent while the Workspace is local-only: there is nothing to rename. */
  onRename?: (section: Section, name: string) => Promise<void>;
  /** Open the section's unpublished Draft (the review screen). */
  onOpenDraft?: (section: Section) => void;
  /** Editing is described to the assistant in the chat; absent hides editing. */
  onBackToChat?: () => void;
  /** Version history and rollback; absent hides the entry. */
  history?: {
    api: Pick<ApiClient, "listVersions" | "draftFromVersion">;
    onDraftReady: (section: Section) => void;
  };
  /** Keywords the assistant routes by (ADR 0236); absent hides the entry. */
  keywords?: {
    api: Pick<ApiClient, "routingProfile" | "editRoutingTerms">;
    onDraftReady: (section: Section) => void;
  };
  now?: Date;
}) {
  const t = useT();
  const { locale } = useLocale();
  const [query, setQuery] = useState("");
  const [renaming, setRenaming] = useState<Section | null>(null);
  const [draftName, setDraftName] = useState("");
  const [renameError, setRenameError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [acting, setActing] = useState<Section | null>(null);
  const [editing, setEditing] = useState<Section | null>(null);
  // A section provided by Mioscape carries a product name; only the owner's
  // own sections are theirs to rename.
  const canRename = (section: Section) =>
    onRename !== undefined && !isSystemSection(section);
  const canEdit = (section: Section) =>
    onBackToChat !== undefined && !isSystemSection(section);
  const hasActions = (section: Section) =>
    canRename(section) || canEdit(section);
  const openRename = (section: Section) => {
    setRenaming(section);
    setDraftName(section.name);
    setRenameError(null);
  };
  const submitRename = async () => {
    if (renaming === null || onRename === undefined) return;
    const name = draftName.trim();
    if (name.split(/\s+/).length > MAX_NAME_WORDS) {
      // The server refuses it too (ADR 0202); nothing is shortened for them.
      setRenameError(t("sections.renameTooLong"));
      return;
    }
    setSaving(true);
    try {
      await onRename(renaming, name);
      setRenaming(null);
    } catch (error) {
      // The sheet stays open: the owner keeps the name they typed.
      setRenameError(t(renameErrorKey(error)));
    } finally {
      setSaving(false);
    }
  };
  const count = (value: number, noun: "record" | "section") =>
    `${String(value)} ${t(`count.${noun}.${pluralCategory(locale, value)}`)}`;
  const subtitle = (section: Section, withUpdate = false) => {
    const records = count(section.record_count ?? 0, "record");
    const parts = isSystemSection(section)
      ? [t("sections.systemSubtitle"), records]
      : [records];
    if (withUpdate && section.updated_at)
      parts.push(
        `${t("sections.updated")} ${daysAgo(locale, new Date(section.updated_at), now)}`,
      );
    return parts.join(" · ");
  };
  const row = (section: Section, text: string) => (
    <li key={section.id} className="relative">
      <ListRow
        title={section.name}
        subtitle={text}
        // The rename control sits where the chevron would: a row is itself a
        // button, so the two cannot nest.
        badge={
          isSystemSection(section) ? (
            <Badge tone="system">{t("nav.system")}</Badge>
          ) : canEdit(section) && section.draft ? (
            <Badge tone="warning">{t("sections.draftBadge")}</Badge>
          ) : undefined
        }
        trailing={
          hasActions(section) ? (
            <span aria-hidden="true" className="block w-9" />
          ) : undefined
        }
        onClick={() => {
          onOpen(section);
        }}
      />
      {hasActions(section) && (
        <span className="absolute right-1 top-1/2 -translate-y-1/2">
          <IconButton
            label={`${t("sections.actions")}: ${section.name}`}
            className="size-11 text-fg-muted"
            onClick={() => {
              setActing(section);
            }}
          >
            <Icons.More size={20} />
          </IconButton>
        </span>
      )}
    </li>
  );
  const group = (label: string, items: ReactNode) => (
    <section aria-label={label} className="flex flex-col gap-2 px-gutter pt-4">
      <GroupLabel>{label}</GroupLabel>
      <Card flush>
        <ul className="overflow-hidden rounded-xl [&>li:last-child>[data-row]]:after:hidden">
          {items}
        </ul>
      </Card>
    </section>
  );
  const needle = query.trim().toLocaleLowerCase(locale);
  const found = needle
    ? sections.filter((section) =>
        section.name.toLocaleLowerCase(locale).includes(needle),
      )
    : [];
  const recent = Object.entries(visits)
    .sort(([, a], [, b]) => b.localeCompare(a))
    .flatMap(([id, at]) => {
      const section = sections.find((item) => item.id === id);
      return section ? [{ section, at }] : [];
    })
    .slice(0, RECENT);
  const system = sections.filter(isSystemSection);
  const mine = sections.filter((section) => !isSystemSection(section));
  return (
    <div className="flex min-h-screen flex-col bg-canvas text-fg">
      <LargeTitle title={t("app.workspace")} className="px-gutter pb-3 pt-4" />
      <div className="px-gutter">
        <label className="relative block">
          <span className="sr-only">{t("sections.search")}</span>
          <Icons.Search
            size={18}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-fg-subtle"
          />
          <input
            type="search"
            value={query}
            placeholder={t("sections.search")}
            onChange={(event) => {
              setQuery(event.target.value);
            }}
            className={`${controlClassName} min-h-12 rounded-lg bg-raised pl-10`}
          />
        </label>
      </div>
      <main className="flex-1 pb-[calc(env(safe-area-inset-bottom)+7rem)]">
        {needle ? (
          found.length > 0 ? (
            group(
              `${t("sections.found")} ${count(found.length, "section")}`,
              found.map((section) => row(section, subtitle(section, true))),
            )
          ) : (
            <EmptyState
              title={t("sections.notFound.title")}
              body={t("sections.notFound.body")}
            />
          )
        ) : (
          <>
            {recent.length > 0 &&
              group(
                t("sections.recent"),
                recent.map(({ section, at }) =>
                  row(section, daysAgo(locale, new Date(at), now)),
                ),
              )}
            {system.length > 0 &&
              group(
                t("sections.system"),
                system.map((section) => row(section, subtitle(section))),
              )}
            {group(
              t("sections.mine"),
              mine.length > 0 ? (
                mine.map((section) => row(section, subtitle(section)))
              ) : (
                <li className="px-4 py-3 text-body text-fg-muted">
                  {t("sections.noneMine")}
                </li>
              ),
            )}
            <section className="px-gutter pt-4">
              <Card flush>
                <ul className="[&>li:last-child>[data-row]]:after:hidden">
                  <li>
                    <ListRow
                      title={t("sections.create")}
                      trailing={<Icons.Plus size={18} />}
                      onClick={onCreate}
                    />
                  </li>
                </ul>
              </Card>
            </section>
          </>
        )}
      </main>
      <Sheet
        open={acting !== null}
        onOpenChange={(open) => {
          if (!open) setActing(null);
        }}
        title={acting?.name ?? ""}
      >
        <div className="flex flex-col gap-2">
          {acting && canRename(acting) && (
            <Button
              variant="secondary"
              onClick={() => {
                openRename(acting);
                setActing(null);
              }}
            >
              {t("sections.rename")}
            </Button>
          )}
          {acting && canEdit(acting) && (
            <Button
              variant="secondary"
              onClick={() => {
                setEditing(acting);
                setActing(null);
              }}
            >
              {t("sections.edit")}
            </Button>
          )}
        </div>
      </Sheet>
      {editing && onBackToChat && (
        <EditSectionSheet
          open
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
          sectionName={editing.name}
          draft={editing.draft}
          onBackToChat={onBackToChat}
          onOpenDraft={() => {
            setEditing(null);
            onOpenDraft?.(editing);
          }}
          history={
            history && {
              api: history.api,
              abilityId: editing.id,
              onDraftReady: () => {
                setEditing(null);
                history.onDraftReady(editing);
              },
            }
          }
          keywords={
            keywords && {
              api: keywords.api,
              abilityId: editing.id,
              onDraftReady: () => {
                setEditing(null);
                keywords.onDraftReady(editing);
              },
            }
          }
        />
      )}
      <Sheet
        open={renaming !== null}
        onOpenChange={(open) => {
          if (!open) setRenaming(null);
        }}
        title={t("sections.renameTitle")}
        description={t("sections.renameHint")}
        footer={
          <Button
            disabled={saving || draftName.trim().length === 0}
            onClick={() => {
              void submitRename();
            }}
          >
            {t("sections.renameSave")}
          </Button>
        }
      >
        <label className="block">
          <span className="sr-only">{t("sections.renameTitle")}</span>
          <Input
            value={draftName}
            maxLength={40}
            onChange={(event) => {
              setDraftName(event.target.value);
            }}
          />
        </label>
        {renameError !== null && (
          <p className="mt-2 text-caption text-danger">{renameError}</p>
        )}
      </Sheet>
    </div>
  );
}
